import React, { useState, useContext } from 'react';
import { Link } from 'gatsby';
import { AnimatePresence } from 'framer-motion';
import useDocumentScrollThrottled from '../hooks/useDocumentScrollThrottled';

import SubNav from '~/components/SubNav';
import NewsletterSubscribe from '~/components/NewsletterSubscribe';

import logoBlack from '~/images/logo.svg';
import logoWhite from '~/images/logo-white.svg';

import camera from '~/images/camera.svg';
import chevronUpWhite from '~/icons/chevron-up-white.svg';
import MobileNav from './MobileNav';
import renderLink from '~/utils/render-link';
import TransContext from '~/contexts/TransContext';
import headerNavs from '~/data/header-navs';

const Header = ({ headerColor = 'white', addBackgroundDefault = false, showHeaderNewsletter = false }) => {
  const trans = useContext(TransContext);
  const [navIsOpen, setNavIsOpen] = useState(false);
  const [subnav, setSubnav] = useState(false);
  const subnavIsOpen = subnav !== false;
  const isWhiteNav = headerColor === 'white' || (headerColor !== 'white' && subnavIsOpen);
  const [shouldAddBackground, setShouldAddBackground] = useState(false);
  const localeHeader = (trans?.lang?.length > 0) ? headerNavs[trans?.lang] : headerNavs['default'];

  const MINIMUM_SCROLL = 80;

  useDocumentScrollThrottled((callbackData) => {
    if (typeof window === 'undefined') return;

    const { currentScrollTop } = callbackData;
    const isMinimumScrolled = currentScrollTop > MINIMUM_SCROLL;

    setShouldAddBackground(isMinimumScrolled);
  });

  return (
    <header
      onMouseEnter={() => setShouldAddBackground(true)}
      onMouseLeave={() => setShouldAddBackground(false)}
      className={`py-0 z-40 transition duration-300 ease-out fixed top-0 w-full
      ${isWhiteNav || shouldAddBackground || addBackgroundDefault ? 'text-black' : 'text-white'} ${
        (subnavIsOpen || navIsOpen) && 'bg-white'
      } ${(shouldAddBackground || addBackgroundDefault) && 'bg-white shadow text-black'} ${
        navIsOpen ? 'open-nav' : ''
      }`}
    >
      {trans?.content?.headerBarHeading && (
        <div className={`${headerColor === 'black' ? 'bg-blue' : 'bg-black'}  text-white py-2`}>
          <div className="container flex items-center justify-center">
            <div className="w-6">
              <img src={camera} alt="" className="object-contain w-full" />
            </div>
            <div className="mx-2 font-bold text-12px">{trans?.content?.headerBarHeading}</div>
            <Link
              to={renderLink(trans?.content?.headerBarLink[0]?.button?.cached_url)}
              className="flex items-center font-bold transition-opacity duration-200 text-12px hover:opacity-70"
            >
              {trans?.content?.headerBarLink[0]?.title}
              <img src={chevronUpWhite} alt="" className="w-2 ml-1 transform rotate-90" />
            </Link>
          </div>
        </div>
      )}

      <div className="container flex items-center justify-between py-2 md:py-0 xl:max-w-full">
        <div className="flex items-center">
          <div className="pr-8 lg:pr-12 xl:pr-16">
            <Link to={renderLink('/')} className="relative block">
              <img
                src={logoBlack}
                className={`${
                  isWhiteNav || shouldAddBackground || addBackgroundDefault || navIsOpen ? 'opacity-100' : 'opacity-0'
                } transition-opacity duration-300 ease-in-out`}
                alt="logo black"
                style={{ maxWidth: '70px' }}
              />
              <img
                className={`absolute inset-0 pointer-events-none ${
                  isWhiteNav || shouldAddBackground || addBackgroundDefault || navIsOpen ? 'opacity-0' : 'opacity-100'
                } transition-opacity duration-300 ease-in-out`}
                src={logoWhite}
                alt="logo white"
                style={{ maxWidth: '70px' }}
              />
            </Link>
          </div>
          <div>
            <nav
              className={`hidden -ml-4 font-medium xl:-ml-5 md:flex text-16px font-heading ${
                isWhiteNav || shouldAddBackground || addBackgroundDefault ? 'text-[#000000]' : 'text-white'
              } `}
            >
              {localeHeader.map((item, i) => (
                <div
                  key={`nav${i}`}
                  className="px-4 xl:px-5"
                  onMouseEnter={() => item.subnav && setSubnav(item.title)}
                  onMouseLeave={() => item.subnav && setSubnav(false)}
                >
                  <Link
                    to={renderLink(item.url)}
                    className={`block my-6 transition duration-150 ease-in-out opacity-100 hover:opacity-60 font-semibold ${
                      !isWhiteNav && 'opacity-75'
                    }`}
                    activeClassName={`opacity-[100] ${
                      isWhiteNav || shouldAddBackground || addBackgroundDefault ? 'text-blue' : 'text-lime'
                    } `}
                  >
                    {item.title}
                  </Link>

                  {/* Subnav */}
                  {item.subnav && (
                    <AnimatePresence>
                      {subnav === item.title && <SubNav subnavName={item.title} {...item.subnav} />}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        <div className="items-center justify-between hidden space-x-8 font-semibold md:flex font-heading text-16px ">
          <a
            href={trans?.content?.headerActions[0]?.button?.cached_url}
            target="_blank"
            className="transition-opacity duration-200 hover:opacity-75"
          >
            {trans?.content?.headerActions[0]?.title}
          </a>
          <div>
            <Link
              to={renderLink(trans?.content?.headerActions[1]?.button?.cached_url)}
              className={`c-btn c-btn--no-icon c-btn--short ${
                isWhiteNav || shouldAddBackground || addBackgroundDefault ? 'c-btn--black' : 'c-btn--primary'
              } transition-colors duration-200 ease-in-out`}
            >
              {trans?.content?.headerActions[1]?.title}
            </Link>
          </div>
        </div>
        <div className="md:hidden">
          <button
            className="md:hidden"
            onClick={() => setNavIsOpen(!!!navIsOpen)}
            className={`${navIsOpen && 'active'}`}
            type="button"
          >
            <div className={`burger ${!isWhiteNav && !shouldAddBackground && 'burger--white'}`}>
              <div />
              <div />
              <div />
            </div>
          </button>
        </div>
      </div>
      {showHeaderNewsletter && (
        <div className="container items-center hidden py-2 border-t border-black sm:flex xl:max-w-full border-opacity-10">
          <NewsletterSubscribe
            className="flex justify-center w-full"
            formContainerClass="flex flex-none w-auto items-center justify-center"
            formClass="ml-4 border-0 border-b-0 border-white max-w-[200px]"
            imgClass="sm:max-w-[50px]"
          />
        </div>
      )}
      {/* <div className="h-[1px] w-full border-t border-black border-opacity-10">
        <div className="w-1/2 h-full bg-blue"></div>
      </div> */}

      <AnimatePresence>
        {navIsOpen && <MobileNav />}
      </AnimatePresence>

      <div
        className={`hidden md:block fixed top-[200px] left-0 right-0 h-screen bg-black pointer-events-none opacity-0 ${
          subnavIsOpen && 'opacity-20 delay-100'
        }  transition-opacity duration-200`}
      ></div>
    </header>
  );
};

export default Header;
